import apiFetch from "@wordpress/api-fetch";

const { createContext, useContext, useState, useMemo, useCallback, useRef } =
    window.React;

const AppContext = createContext({
    postsPromise: Promise.resolve([]),
    postsRef: null,
    categoriesFiltered: [],
    resourceTypesFiltered: [],
    handleToggle: () => {},
    resetFilters: () => {},
    page: 1,
    maxPages: 0,
    isBusy: true,
    onLoadMore: () => {},
    loadMoreRef: () => {},
    mobileFilterOpen: false,
    toggleMobileFilter: () => {},
    isDebugEnabled: false,
    lastEndpoint: "",
});

export function useAppContext() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useAppContext must be used within AppProvider");
    }
    return context;
}

const loadMoreObserver = new IntersectionObserver(
    (entries) => {
        entries.forEach(
            ({ target, isIntersecting }) => isIntersecting && target.click(),
        );
    },
    { root: null, rootMargin: "720px", threshold: [0] },
);

export function AppProvider({ children }) {
    const isDebugEnabled = useMemo(
        () =>
            new URL(window.location.href).searchParams.has("debug") &&
            window.adw?.is_user_logged_in,
        [],
    );

    const postsRef = useRef([]);

    const [
        {
            page,
            maxPages,
            isBusy,
            mobileFilterOpen,
            categoriesFiltered,
            resourceTypesFiltered,
            lastEndpoint,
        },
        setState,
    ] = useState({
        page: 1,
        maxPages: 0,
        isBusy: true,
        mobileFilterOpen: false,
        categoriesFiltered: [],
        resourceTypesFiltered: [],
        lastEndpoint: "",
    });

    const loadMoreRef = useCallback((btnNode) => {
        if (btnNode) {
            loadMoreObserver.observe(btnNode);
            return;
        }
        loadMoreObserver?.disconnect();
    }, []);

    const onLoadMore = useCallback(() => {
        if (isBusy) return;

        setState((prev) => {
            if (prev.page + 1 <= prev.maxPages) {
                return { ...prev, page: prev.page + 1, isBusy: true };
            }
            return prev;
        });
    }, [isBusy]);

    const handleToggle = useCallback(({ id, taxonomy }) => {
        postsRef.current = [];
        setState((prev) => {
            const key =
                taxonomy === "category"
                    ? "categoriesFiltered"
                    : "resourceTypesFiltered";
            const current = prev[key];
            const updated = current.includes(id)
                ? current.filter((filterId) => filterId !== id)
                : current.concat(id);

            prev.page = 1;
            prev.isBusy = true;
            prev[key] = updated;

            return structuredClone(prev);
        });
    }, []);

    const resetFilters = useCallback(() => {
        postsRef.current = [];
        setState((prev) => ({
            ...prev,
            page: 1,
            maxPages: 0,
            isBusy: true,
            categoriesFiltered: [],
            resourceTypesFiltered: [],
        }));
    }, []);

    const toggleMobileFilter = useCallback((open) => {
        setState((prev) => ({
            ...prev,
            mobileFilterOpen:
                typeof open === "boolean" ? open : !prev.mobileFilterOpen,
        }));
    }, []);

    const postsPromise = useMemo(async () => {
        const urlEndpoint = new URL(
            window.location.origin + `/wp-json/adw-resource-hub/v1/posts`,
        );

        urlEndpoint.searchParams.set("page", page);
        urlEndpoint.searchParams.set("categories", categoriesFiltered);
        urlEndpoint.searchParams.set("resource_types", resourceTypesFiltered);

        if (isDebugEnabled) {
            setState((prev) => ({ ...prev, lastEndpoint: urlEndpoint.toString() }));
        }

        return apiFetch({
            path: urlEndpoint.toString(),
            method: "GET",
        })
            .then(({ posts: newPosts = [], max_num_pages = 1 }) => {
                postsRef.current = postsRef.current.concat(newPosts);
                setState((prev) => ({
                    ...prev,
                    maxPages: max_num_pages ?? 1,
                    isBusy: false,
                }));
                return postsRef.current;
            })
            .catch((err) => {
                console.error("Error fetching resource hub posts:", err);
                setState((prev) => ({ ...prev, isBusy: false }));
                postsRef.current = [];
                return postsRef.current;
            });
    }, [page, categoriesFiltered, resourceTypesFiltered, isDebugEnabled]);

    const value = useMemo(
        () => ({
            postsPromise,
            postsRef: postsRef,

            // Filters
            categoriesFiltered,
            resourceTypesFiltered,
            handleToggle,
            resetFilters,

            // Pagination
            page,
            maxPages,
            isBusy,
            onLoadMore,
            loadMoreRef,

            // Mobile
            mobileFilterOpen,
            toggleMobileFilter,

            // Debug
            isDebugEnabled,
            lastEndpoint,
        }),
        [
            postsRef,
            postsPromise,
            categoriesFiltered,
            resourceTypesFiltered,
            handleToggle,
            resetFilters,
            page,
            maxPages,
            isBusy,
            onLoadMore,
            loadMoreRef,
            mobileFilterOpen,
            toggleMobileFilter,
            isDebugEnabled,
            lastEndpoint,
        ],
    );

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export default AppContext;
